import { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { directorAPI, projectsAPI } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send, Sparkles, Trash2, ArrowRight, Loader2, Zap } from "lucide-react";

const TXT = {
    fr: {
        button: "Parler au Directeur",
        title: "Directeur Masqué",
        subtitle_project: "Contexte : projet actif",
        subtitle_no_project: "Aucun projet sélectionné",
        placeholder: "Ex : Je ne sais pas quelle niche choisir…",
        greeting: "Bonjour. Pose-moi ta question — je te réponds en 3 lignes : diagnostic, recommandation, action.",
        clear: "Vider l'historique",
        send: "Envoyer",
        sending: "…",
        error: "Le Directeur n'a pas pu répondre. Réessaie.",
        empty_tip: "Astuce : demande-moi une niche, un hook, ou 3 idées de Shorts.",
        action_create_project: "Créer le projet",
        action_generate_short: "Générer ce Short",
        action_open_module: "Ouvrir",
        action_improve_hook: "Générer 3 hooks",
    },
    en: {
        button: "Talk to the Director",
        title: "Masked Director",
        subtitle_project: "Context: active project",
        subtitle_no_project: "No project selected",
        placeholder: "e.g. I don't know which niche to pick…",
        greeting: "Hello. Ask me a question — I'll answer in 3 lines: diagnosis, recommendation, action.",
        clear: "Clear history",
        send: "Send",
        sending: "…",
        error: "The Director couldn't reply. Retry.",
        empty_tip: "Tip: ask me for a niche, a hook, or 3 Short ideas.",
        action_create_project: "Create project",
        action_generate_short: "Generate this Short",
        action_open_module: "Open",
        action_improve_hook: "Generate 3 hooks",
    },
    es: {
        button: "Hablar con el Director",
        title: "Director Enmascarado",
        subtitle_project: "Contexto: proyecto activo",
        subtitle_no_project: "Ningún proyecto seleccionado",
        placeholder: "Ej: No sé qué nicho elegir…",
        greeting: "Hola. Hazme una pregunta — te respondo en 3 líneas: diagnóstico, recomendación, acción.",
        clear: "Borrar historial",
        send: "Enviar",
        sending: "…",
        error: "El Director no pudo responder. Reintenta.",
        empty_tip: "Consejo: pídeme un nicho, un hook, o 3 ideas de Shorts.",
        action_create_project: "Crear proyecto",
        action_generate_short: "Generar este Short",
        action_open_module: "Abrir",
        action_improve_hook: "Generar 3 hooks",
    },
};

export default function DirectorChat({ onNavigate }) {
    const { lang, user, activeProjectId, projects, refreshProjects } = useApp();
    const t = TXT[lang] || TXT.fr;
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    const activeProject = projects.find((p) => p.id === activeProjectId);

    useEffect(() => {
        if (!open || !user) return;
        (async () => {
            try {
                const d = await directorAPI.history(activeProjectId);
                setMessages(d.messages || []);
            } catch { setMessages([]); }
        })();
    }, [open, activeProjectId, user]);

    // External triggers: any module can request opening the panel with a preset prompt
    useEffect(() => {
        const handler = (e) => {
            setOpen(true);
            const preset = e?.detail?.prompt;
            if (preset) setInput(preset);
        };
        window.addEventListener("sf:open-director", handler);
        return () => window.removeEventListener("sf:open-director", handler);
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, busy]);

    const send = async () => {
        const q = input.trim();
        if (!q || busy) return;
        setInput("");
        setBusy(true);
        const localUserMsg = { role: "user", content: q, created_at: new Date().toISOString() };
        setMessages((m) => [...m, localUserMsg]);
        try {
            const r = await directorAPI.chat(q, activeProjectId, lang);
            setMessages((m) => [...m, { role: "assistant", content: r.reply.message, actions: r.reply.actions, created_at: new Date().toISOString() }]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t.error);
        } finally { setBusy(false); }
    };

    const clearHistory = async () => {
        try { await directorAPI.clear(activeProjectId); setMessages([]); } catch {}
    };

    const runAction = async (a) => {
        try {
            if (a.kind === "create_project") {
                await projectsAPI.create({
                    name: a.payload.name || "Nouveau projet",
                    niche: a.payload.niche || "",
                    positioning: a.payload.positioning || "",
                    character: a.payload.character || "",
                    quota_allocated: 0,
                });
                toast.success("✓ " + (a.label || "Projet créé"));
                await refreshProjects();
                onNavigate?.("projects");
            } else if (a.kind === "open_module") {
                onNavigate?.(a.payload.module || "projects");
                setOpen(false);
            } else if (a.kind === "generate_short") {
                // Pipe the idea into the Episode module via URL hash / event
                window.dispatchEvent(new CustomEvent("sf:director-fill-episode", { detail: a.payload }));
                onNavigate?.("episode");
                setOpen(false);
                toast.success("✓ Idée transmise à l'onglet Épisodes");
            } else if (a.kind === "improve_hook") {
                window.dispatchEvent(new CustomEvent("sf:director-fill-episode", { detail: { idea: `Améliore ce hook : ${a.payload.current_hook}` } }));
                onNavigate?.("episode");
                setOpen(false);
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Action error"); }
    };

    const actionLabel = (kind) => ({
        create_project: t.action_create_project,
        generate_short: t.action_generate_short,
        open_module: t.action_open_module,
        improve_hook: t.action_improve_hook,
    }[kind] || kind);

    if (!user) return null;

    return (
        <>
            <button
                data-testid="director-fab"
                onClick={() => setOpen(true)}
                className={`fixed bottom-6 right-6 z-40 flame-gradient text-black font-semibold px-5 py-3.5 rounded-full shadow-2xl kinetic flex items-center gap-2 flame-glow ${open ? "opacity-0 pointer-events-none" : ""}`}
                aria-label={t.button}
            >
                <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
                <span className="hidden sm:inline">{t.button}</span>
            </button>

            {open && (
                <div
                    data-testid="director-panel"
                    className="fixed inset-0 z-50 sm:inset-auto sm:bottom-0 sm:right-0 sm:top-0 sm:w-[440px] bg-[#050914] sm:bg-transparent"
                >
                    <div className="h-full flex flex-col bg-gradient-to-b from-[#0F141C] via-[#080B10] to-[#050914] sm:border-l sm:border-[#253046] sm:shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-[#253046] shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full flame-gradient grid place-items-center">
                                    <Sparkles className="w-5 h-5 text-black" strokeWidth={2.2} />
                                </div>
                                <div>
                                    <div className="font-display uppercase text-sm font-bold">{t.title}</div>
                                    <div className="text-[10px] font-mono-sf text-slate-500 tracking-widest">
                                        {activeProject ? `${t.subtitle_project} · ${activeProject.name}` : t.subtitle_no_project}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {messages.length > 0 && (
                                    <button data-testid="director-clear" onClick={clearHistory} className="text-slate-400 hover:text-red-400 p-2" title={t.clear}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                                <button data-testid="director-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-2">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div ref={scrollRef} data-testid="director-messages" className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.length === 0 && !busy && (
                                <div className="rounded-lg bg-[#0B111B] border border-[#253046] p-4">
                                    <div className="flex items-start gap-2">
                                        <Sparkles className="w-4 h-4 text-[#FF4D00] mt-0.5 shrink-0" />
                                        <div className="text-sm text-slate-300 leading-relaxed">{t.greeting}</div>
                                    </div>
                                    <div className="mt-2 text-xs text-slate-500 italic">{t.empty_tip}</div>
                                </div>
                            )}

                            {messages.map((m, i) => (
                                <div key={i} data-testid={`director-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[85%] rounded-lg p-3 ${m.role === "user" ? "bg-[#FF4D00]/15 border border-[#FF4D00]/30 text-slate-100" : "bg-[#0B111B] border border-[#253046] text-slate-200"}`}>
                                        <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
                                        {m.role === "assistant" && m.actions?.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {m.actions.map((a, j) => (
                                                    <button
                                                        key={j}
                                                        data-testid={`director-action-${a.kind}`}
                                                        onClick={() => runAction(a)}
                                                        className="text-xs px-3 py-1.5 rounded flame-gradient text-black font-semibold flex items-center gap-1.5 kinetic hover:brightness-110"
                                                    >
                                                        {a.kind === "create_project" ? <Zap className="w-3 h-3" fill="currentColor" /> : <ArrowRight className="w-3 h-3" />}
                                                        {a.label || actionLabel(a.kind)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {busy && (
                                <div className="flex justify-start">
                                    <div className="rounded-lg p-3 bg-[#0B111B] border border-[#253046]">
                                        <Loader2 className="w-4 h-4 text-[#FF4D00] animate-spin" />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input */}
                        <div className="p-4 border-t border-[#253046] shrink-0">
                            <div className="flex gap-2">
                                <input
                                    data-testid="director-input"
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && send()}
                                    placeholder={t.placeholder}
                                    disabled={busy}
                                    className="flex-1 bg-[#0B111B] border border-[#253046] focus:border-[#FF4D00] rounded-md px-3 py-2.5 text-white text-sm outline-none kinetic"
                                />
                                <button
                                    data-testid="director-send"
                                    onClick={send}
                                    disabled={busy || !input.trim()}
                                    className="flame-gradient text-black font-semibold px-4 rounded-md flex items-center gap-1.5 kinetic disabled:opacity-30"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
